const newRowId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const AI_FIELD_VALUE = '[ai_field]';

export const isRowAiGenerated = (row) => Boolean(row.showDescription);

export const emptyHeaderRow = () => ({
  id: newRowId('hdr'),
  name: '',
  value: '',
  description: '',
  showDescription: false
});

export const emptyBodyRow = () => ({
  id: newRowId('body'),
  name: '',
  value: '',
  description: '',
  showDescription: false
});

export const emptyQueryParamRow = () => ({
  id: newRowId('qp'),
  name: '',
  value: '',
  description: '',
  showDescription: false
});

const rowFromArrayEntry = (item, createId) => {
  if (!item || typeof item !== 'object') return null;
  const name = typeof item.name === 'string' ? item.name : '';
  if (!name.trim()) return null;

  const description = typeof item.description === 'string' ? item.description : '';
  const rawValue = typeof item.value === 'string' ? item.value : '';
  const isAiField = rawValue === AI_FIELD_VALUE;

  return {
    id: createId(),
    name,
    value: isAiField ? '' : rawValue,
    description,
    showDescription: isAiField || Boolean(description.trim())
  };
};

export const headersFromPayload = (headers) => {
  if (headers && typeof headers === 'object' && !Array.isArray(headers)) {
    const rows = Object.entries(headers)
      .map(([name, value]) => {
        if (!name.trim()) return null;
        return {
          id: newRowId('hdr'),
          name,
          value: value === AI_FIELD_VALUE ? '' : (typeof value === 'string' ? value : String(value ?? '')),
          description: '',
          showDescription: value === AI_FIELD_VALUE
        };
      })
      .filter(Boolean);

    return rows.length > 0 ? rows : [emptyHeaderRow()];
  }

  if (!Array.isArray(headers)) return [emptyHeaderRow()];

  const rows = headers.map((item) => rowFromArrayEntry(item, () => newRowId('hdr'))).filter(Boolean);
  return rows.length > 0 ? rows : [emptyHeaderRow()];
};

export const headersToPayload = (headerRows = []) => {
  const record = {};

  headerRows.forEach((row) => {
    const name = (row.name || '').trim();
    if (!name) return;
    record[name] = isRowAiGenerated(row) ? AI_FIELD_VALUE : (row.value || '').trim();
  });

  return record;
};

export const BODY_PAYLOAD_TYPE = 'json';

export const BODY_MODES = {
  PARAMS: 'params',
  JSON: 'json'
};

const rowFromValueEntry = (item, createId) => {
  if (!item || typeof item !== 'object') return null;
  const name = typeof item.name === 'string' ? item.name : '';
  if (!name.trim()) return null;

  const aiGenerated = item.ai_generated === true;
  const aiDescription = typeof item.ai_description === 'string'
    ? item.ai_description
    : (typeof item.description === 'string' ? item.description : '');
  const rawValue = item.value == null ? '' : String(item.value);
  const isAiPlaceholder = rawValue === AI_FIELD_VALUE;

  return {
    id: createId(),
    name,
    value: (aiGenerated || isAiPlaceholder) ? '' : rawValue,
    description: aiDescription,
    showDescription: aiGenerated
  };
};

const toParamEntry = (row) => {
  const aiGenerated = isRowAiGenerated(row);
  const entry = {
    name: row.name.trim(),
    ai_generated: aiGenerated,
    value: aiGenerated ? AI_FIELD_VALUE : ((row.value || '').trim() || null)
  };

  if (aiGenerated) {
    const aiDescription = (row.description || '').trim();
    if (aiDescription) entry.ai_description = aiDescription;
  }

  return entry;
};

const hasJsonBodyContent = (body) => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
  if (body.content == null) return false;
  if (typeof body.content === 'string') return body.content.trim().length > 0;
  return typeof body.content === 'object';
};

const serializeJsonBodyContent = (content) => {
  if (typeof content === 'string') return content;
  try {
    return JSON.stringify(content, null, 2);
  } catch (_e) {
    return '';
  }
};

export const bodyFromPayload = (body) => {
  if (hasJsonBodyContent(body)) {
    return {
      bodyMode: BODY_MODES.JSON,
      bodyJsonContent: serializeJsonBodyContent(body.content),
      bodyRows: [emptyBodyRow()]
    };
  }

  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return {
      bodyMode: BODY_MODES.PARAMS,
      bodyJsonContent: '',
      bodyRows: [emptyBodyRow()]
    };
  }

  const values = Array.isArray(body.values) ? body.values : [];
  const rows = values.map((item) => rowFromValueEntry(item, () => newRowId('body'))).filter(Boolean);

  return {
    bodyMode: BODY_MODES.PARAMS,
    bodyJsonContent: '',
    bodyRows: rows.length > 0 ? rows : [emptyBodyRow()]
  };
};

export const isBodyRowAiGenerated = isRowAiGenerated;

export const bodyParamsToPayload = (bodyRows = []) => ({
  type: BODY_PAYLOAD_TYPE,
  values: bodyRows.filter((row) => (row.name || '').trim()).map(toParamEntry)
});

/** Hydrates query-param rows from saved tool args (params-only; no JSON mode). */
export const queryParamsFromPayload = (queryParams) => {
  if (!Array.isArray(queryParams)) return [emptyQueryParamRow()];

  const rows = queryParams
    .map((item) => rowFromValueEntry(item, () => newRowId('qp')))
    .filter(Boolean);

  return rows.length > 0 ? rows : [emptyQueryParamRow()];
};

export const queryParamsToPayload = (queryParamRows = []) => queryParamRows
  .filter((row) => (row.name || '').trim())
  .map(toParamEntry);

export const tryParseJsonBodyContent = (rawContent) => {
  const trimmed = (rawContent || '').trim();
  if (!trimmed) {
    return { ok: true, content: null };
  }

  try {
    return { ok: true, content: JSON.parse(trimmed) };
  } catch (_e) {
    return { ok: false, errorKey: 'tools.retailer_api_request_errors.body_json_invalid' };
  }
};

export const bodyToPayload = (draft) => {
  if (draft.bodyMode === BODY_MODES.JSON) {
    const parsed = tryParseJsonBodyContent(draft.bodyJsonContent);
    if (!parsed.ok) return parsed;

    return {
      ok: true,
      body: {
        type: BODY_PAYLOAD_TYPE,
        content: parsed.content
      }
    };
  }

  return {
    ok: true,
    body: bodyParamsToPayload(draft.bodyRows || [])
  };
};

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export const RETAILER_AUTH_METHOD_NONE_ID = 'none';

export const emptyRetailerApiRequestDraft = () => ({
  requestUrl: '',
  httpMethod: 'GET',
  headerRows: [emptyHeaderRow()],
  queryParamRows: [emptyQueryParamRow()],
  bodyMode: BODY_MODES.PARAMS,
  bodyRows: [emptyBodyRow()],
  bodyJsonContent: ''
});

/** Hydrates API request form state + auth dropdown from saved tool args. */
export const draftFromRetailerApiRequestArgs = (args = []) => {
  const args0 = Array.isArray(args) ? args[0] : null;
  if (!args0 || typeof args0 !== 'object') {
    return { draft: emptyRetailerApiRequestDraft(), authMethodId: RETAILER_AUTH_METHOD_NONE_ID };
  }

  const rawId = String(args0.id || args0.retailer_auth_method_id || '').trim();
  const bodyState = bodyFromPayload(args0.body);

  return {
    draft: {
      requestUrl: typeof args0.request_url === 'string' ? args0.request_url : '',
      httpMethod: typeof args0.http_method === 'string' ? args0.http_method.toUpperCase() : 'GET',
      headerRows: headersFromPayload(args0.headers),
      queryParamRows: queryParamsFromPayload(args0.query_params),
      bodyMode: bodyState.bodyMode,
      bodyRows: bodyState.bodyRows,
      bodyJsonContent: bodyState.bodyJsonContent
    },
    authMethodId: rawId || RETAILER_AUTH_METHOD_NONE_ID
  };
};

/**
 * @param {object} draft
 * @returns {{ ok: true, payload: object } | { ok: false, errorKey: string }}
 */
export const tryBuildRetailerApiRequestPayload = (draft) => {
  const requestUrl = (draft.requestUrl || '').trim();
  if (!requestUrl) {
    return { ok: false, errorKey: 'tools.retailer_api_request_errors.url_required' };
  }

  const httpMethod = (draft.httpMethod || '').trim().toUpperCase();
  if (!HTTP_METHODS.includes(httpMethod)) {
    return { ok: false, errorKey: 'tools.retailer_api_request_errors.method_invalid' };
  }

  const bodyResult = bodyToPayload(draft);
  if (!bodyResult.ok) {
    return bodyResult;
  }

  const id = String(draft.authMethodId || RETAILER_AUTH_METHOD_NONE_ID).trim() || RETAILER_AUTH_METHOD_NONE_ID;

  return {
    ok: true,
    payload: {
      id,
      request_url: requestUrl,
      http_method: httpMethod,
      headers: headersToPayload(draft.headerRows || []),
      query_params: queryParamsToPayload(draft.queryParamRows || []),
      body: bodyResult.body
    }
  };
};
